import { Tag, baseApi } from './base'
import { getSocket } from './socket'

import { Availability, CreateAvailabilityDto, MeetingLink, Unavailability, UpdateMeetingLinkDto } from '@/core/models/meeting-link.model'
import { MeetingLinkOrder } from '@/core/models/meeting-link-order.model'
import { SearchMeetingLinksQuery } from '@/core/types/request'
import { SearchResponse } from '@/core/types/response'
import { toSearchResponse } from '@/core/utils/list-response.util'
import { PaymentConfig } from '@/core/models/payment.model'

import { cacher } from '@/utils/rtkQueryCacheUtils'

export type UpdateMeetingLinkParams = UpdateMeetingLinkDto & { meetingLinkId: string }

export type CreateUnavailabilityParams = {
  meetingLinkId: string
  availabilityId: string
  body: {
    startDate: string
    endDate: string
    startTime?: number
    endTime?: number
    reason?: string
    cancelAffectedOrders?: boolean
  }
}

export type DeleteUnavailabilityParams = {
  meetingLinkId: string
  availabilityId: string
  unavailabilityId: string
}

export type PreviewUnavailabilityParams = {
  meetingLinkId: string
  availabilityId: string
  body: {
    startDate: string
    endDate: string
    startTime?: number
    endTime?: number
  }
}

export type CreateAvailabilityParams = {
  meetingLinkId: string
  availability: CreateAvailabilityDto & {
    paymentConfig?: PaymentConfig
  }
}

export const meetingLinkApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createMeetingLinkAvailability: builder.mutation<Availability, CreateAvailabilityParams>({
      query: ({ meetingLinkId, availability }) => ({
        url: `/meeting-links/${meetingLinkId}/availabilities`,
        method: 'POST',
        body: availability,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.MEETING_LINK, id: arg.meetingLinkId }],
    }),
    getMeetingLink: builder.query<MeetingLink, string>({
      query: (meetingLinkId) => `/meeting-links/${meetingLinkId}`,
      providesTags: (_result, _error, meetingLinkId) => [{ type: Tag.MEETING_LINK, id: meetingLinkId }],
    }),
    getMeetingLinks: builder.query<SearchResponse<MeetingLink>, SearchMeetingLinksQuery & { limit?: number; skip?: number }>({
      query: (query) => ({
        url: '/meeting-links',
        method: 'GET',
        params: { ...query },
      }),
      transformResponse: (response: MeetingLink[] | SearchResponse<MeetingLink>) => toSearchResponse(response, 'GET /meeting-links'),
      providesTags: (result, error) => cacher.providesList(Tag.MEETING_LINK)(result?.items, error as never),
    }),
    updateMeetingLink: builder.mutation<MeetingLink, UpdateMeetingLinkParams>({
      query: ({ meetingLinkId, ...body }) => ({
        url: `/meeting-links/${meetingLinkId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.MEETING_LINK, id: arg.meetingLinkId }],
    }),
    updateAvailability: builder.mutation<Availability, { meetingLinkId: string; availabilityId: string; availability: Partial<CreateAvailabilityDto & { paymentConfig?: PaymentConfig }> }>({
      query: ({ meetingLinkId, availabilityId, availability }) => ({
        url: `/meeting-links/${meetingLinkId}/availabilities/${availabilityId}`,
        method: 'PATCH',
        body: availability,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.MEETING_LINK, id: arg.meetingLinkId }],
    }),
    deleteMeetingLink: builder.mutation<MeetingLink, string>({
      query: (meetingLinkId) => ({
        url: `/meeting-links/${meetingLinkId}`,
        method: 'DELETE',
      }),
    }),
    enterMeetingLink: builder.mutation<string, string>({
      queryFn: async (meetingLinkId) => {
        const socket = await getSocket()

        socket.emit('enter_meeting_link', meetingLinkId)

        return { data: '' }
      },
    }),
    leaveMeetingLink: builder.mutation<string, string>({
      queryFn: async (meetingLinkId) => {
        const socket = await getSocket()
        socket.emit('leave_meeting_link', meetingLinkId)
        return { data: '' }
      },
    }),
    createUnavailability: builder.mutation<{ meetingLink: MeetingLink; canceledOrdersCount: number }, CreateUnavailabilityParams>({
      query: ({ meetingLinkId, availabilityId, body }) => ({
        url: `/meeting-links/${meetingLinkId}/availabilities/${availabilityId}/unavailabilities`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.MEETING_LINK, id: arg.meetingLinkId }],
    }),
    deleteUnavailability: builder.mutation<MeetingLink, DeleteUnavailabilityParams>({
      query: ({ meetingLinkId, availabilityId, unavailabilityId }) => ({
        url: `/meeting-links/${meetingLinkId}/availabilities/${availabilityId}/unavailabilities/${unavailabilityId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: Tag.MEETING_LINK, id: arg.meetingLinkId }],
    }),
    previewUnavailability: builder.mutation<{ affectedOrders: MeetingLinkOrder[] }, PreviewUnavailabilityParams>({
      query: ({ meetingLinkId, availabilityId, body }) => ({
        url: `/meeting-links/${meetingLinkId}/availabilities/${availabilityId}/unavailabilities/preview`,
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
})
